import React from 'react';
import { Card, Descriptions, Badge, Alert, Divider, Typography } from 'antd';
import { SafetyCertificateOutlined, FireOutlined } from '@ant-design/icons';
import styles from './Security.module.scss';

const { Title, Paragraph } = Typography;

const SecurityInfo = ({ securityInfo }) => {
  // SELinux 정보 파싱
  const parseSelinuxInfo = (data) => {
    if (!data || data === 'N/A') return { status: 'Unknown', mode: 'Unknown' };
    
    try {
      let status = 'Unknown';
      let mode = 'Unknown';
      
      const lines = data.trim().split('\n');
      
      lines.forEach(line => {
        if (line.includes('SELinux status:')) {
          status = line.split(':')[1].trim();
        } else if (line.includes('Current mode:')) {
          mode = line.split(':')[1].trim();
        }
      });
      
      return { status, mode };
    } catch (error) {
      console.error('Error parsing SELinux info:', error);
      return { status: 'Error', mode: 'Error' };
    }
  };
  
  // 방화벽 정보 파싱
  const parseFirewallInfo = (data) => {
    if (!data || data === 'N/A') return { status: 'Unknown', zones: [] };
    
    try {
      let status = 'Unknown';
      const zones = [];
      let currentZone = null;
      
      const lines = data.trim().split('\n');
      
      lines.forEach(line => {
        if (line.includes('FirewallD is')) {
          status = line.includes('running') ? 'running' : 'not running';
        } else if (line.match(/^[a-z]+\s+\(.*\)/)) {
          // 새 존 시작
          const zoneName = line.split(/\s+/)[0];
          currentZone = { name: zoneName, interfaces: [], services: [], ports: [] };
          zones.push(currentZone);
        } else if (currentZone) {
          if (line.includes('interfaces:')) {
            const interfaces = line.replace('interfaces:', '').trim().split(/\s+/);
            if (interfaces[0]) currentZone.interfaces = interfaces;
          } else if (line.includes('services:')) {
            const services = line.replace('services:', '').trim().split(/\s+/);
            if (services[0]) currentZone.services = services;
          } else if (line.includes('ports:')) {
            const ports = line.replace('ports:', '').trim().split(/\s+/);
            if (ports[0]) currentZone.ports = ports;
          }
        }
      });
      
      return { status, zones };
    } catch (error) {
      console.error('Error parsing firewall info:', error);
      return { status: 'Error', zones: [] };
    }
  };

  const selinux = parseSelinuxInfo(securityInfo?.selinux);
  const firewall = parseFirewallInfo(securityInfo?.firewall);

  return (
    <div className={styles.securityContainer}>
      <Card 
        title={
          <div className={styles.cardTitle}>
            <SafetyCertificateOutlined className={styles.titleIcon} />
            SELinux 상태
          </div>
        } 
        className={styles.securityCard}
      >
        <Descriptions bordered>
          <Descriptions.Item label="SELinux 상태" span={3}>
            <Badge 
              status={selinux.status === 'enabled' ? 'success' : 'error'} 
              text={selinux.status} 
            />
          </Descriptions.Item>
          <Descriptions.Item label="현재 모드" span={3}>
            <Badge 
              status={
                selinux.mode === 'enforcing' ? 'success' : 
                selinux.mode === 'permissive' ? 'warning' : 'error'
              } 
              text={selinux.mode} 
            />
          </Descriptions.Item>
        </Descriptions>
        
        {selinux.status === 'disabled' && (
          <Alert 
            message="보안 경고" 
            description="SELinux가 비활성화되어 있습니다. 보안 강화를 위해 활성화하는 것을 권장합니다." 
            type="warning" 
            showIcon 
            className={styles.securityAlert}
          />
        )}
      </Card>
      
      <Card 
        title={
          <div className={styles.cardTitle}>
            <FireOutlined className={styles.titleIcon} />
            방화벽 상태
          </div>
        } 
        className={styles.securityCard}
      >
        <Descriptions bordered>
          <Descriptions.Item label="방화벽 상태" span={3}>
            <Badge 
              status={firewall.status === 'running' ? 'success' : 'error'} 
              text={firewall.status === 'running' ? '실행 중' : '중지됨'} 
            />
          </Descriptions.Item>
        </Descriptions>
        
        {firewall.status !== 'running' && (
          <Alert 
            message="보안 경고" 
            description="방화벽이 실행되고 있지 않습니다. 보안 강화를 위해 방화벽을 활성화하는 것을 권장합니다." 
            type="warning" 
            showIcon 
            className={styles.securityAlert}
          />
        )}
        
        {firewall.zones.length > 0 && (
          <div className={styles.zonesContainer}>
            <Divider orientation="left">방화벽 존</Divider>
            
            {firewall.zones.map((zone, index) => (
              <div key={index} className={styles.zoneItem}>
                <Title level={5}>{zone.name}</Title>
                
                <Paragraph>
                  <strong>인터페이스:</strong> {zone.interfaces.length > 0 ? zone.interfaces.join(', ') : '없음'}
                </Paragraph>
                
                <Paragraph>
                  <strong>서비스:</strong> {zone.services.length > 0 ? zone.services.join(', ') : '없음'}
                </Paragraph>
                
                <Paragraph>
                  <strong>포트:</strong> {zone.ports.length > 0 ? zone.ports.join(', ') : '없음'}
                </Paragraph>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default SecurityInfo;